import React, { useEffect, useState, useRef } from 'react';
import Layout from '../../components/Layout';
import useService from '../../hooks/useService';
import { UserService } from '../../service/UserService';
import { PROFILE_PAGE } from '../../constants/route.const';
import SKButton from '../../components/UI/SKButton';
import { StorageService } from '../../service/Storage';
import { PinInput, PinInputField } from '@chakra-ui/pin-input';
import { differenceInSeconds } from 'date-fns';
import { useToast } from '@chakra-ui/toast';
import { useInterval } from '../../hooks/useInterval';
import { useAutoLogin } from 'hooks/use-auto-login';
import {
  GOOGLE_RECAPTCHA_BASE_URL,
  RECAPTCHA_KEY,
  loadScript,
  logout,
  secondsToMinutes,
} from '../../utils';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Input,
  FormLabel,
  Text,
  useMediaQuery,
  Spinner,
} from '@chakra-ui/react';
import CTA from '../../components/UI/CTA';
import clsx from 'clsx';
import classes from './styles.module.scss';
import { BannedContactSupport } from 'components/AccountBlocked';

export default function Auth() {
  const [mobileNumber, setMobileNumber] = useState('');
  const [isVerifying, setIsVerifying] = useState(true);
  const [otp, setOtp] = useState('');
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [accountBlocked, setAccountBlocked] = useState(false);
  const [resendSmsAt, setResendSmsAt] = useState();
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const inputRef = useRef();

  const otpCtrl = useService(UserService.getOTPwithRecaptcha);
  const loginCtrl = useService(UserService.login);
  const toast = useToast();

  let paramMobile = useSearchParams()[0].get('mobile');
  let refreshToken = useSearchParams()[0].get('rt');
  let currency = useSearchParams()[0].get('currency');
  const navigate = useNavigate();

  useAutoLogin({
    setShowOtpForm: setShowOtpForm,
    setAccountBlocked: setAccountBlocked,
  });
  //function to validate api response for ban
  const validateBan = e => {
    if (e.data?.error_code === 1003 || e.data?.error_code === 1004) {
      setShowOtpForm(false);
      setAccountBlocked(true);
      setIsOtpLoading(false);
      return;
    }
  };
  async function initRecaptcha() {
    const isLoaded = await loadScript(
      GOOGLE_RECAPTCHA_BASE_URL + RECAPTCHA_KEY
    );
    if (isLoaded) {
      window.grecaptcha.enterprise.ready(() => {});
    } else {
      console.log('Recaptcha failed to load');
    }
  }
  async function generateRecaptchaToken() {
    let token;
    if (window.grecaptcha) {
      token = await window.grecaptcha.enterprise.execute(RECAPTCHA_KEY, {
        action: 'otp',
      });
    } else {
      initRecaptcha();
    }
    return token;
  }
  useEffect(() => {
    if (accountBlocked && !paramMobile && !mobileNumber) {
      setMobileNumber(StorageService.getMobile());
      logout();
    }
  }, [accountBlocked]);

  useEffect(() => {
    initRecaptcha();
    const setCurrency = () => {
      if (currency) StorageService.setCurrency(currency);
    };
    setCurrency();
    if (refreshToken) {
      StorageService.setSessionFrom('app');
      return setIsVerifying(false);
    }
    if (!StorageService.getAccessToken()) return setIsVerifying(false);
    if (!StorageService.getRefreshToken()) return setIsVerifying(false);
    if (!StorageService.getMobile()) return setIsVerifying(false);
    const validateTokensAndRedirect = async () => {
      try {
        const res = await UserService.getTokenForAutoLogin(
          StorageService.getRefreshToken()
        );
        StorageService.setAccessToken(res.access_token);
        StorageService.setRefreshToken(res.refresh_token);
        navigate(PROFILE_PAGE, { replace: true });
      } catch (e) {
        setIsVerifying(false);
        validateBan(e);
      }
    };
    if (!paramMobile) {
      validateTokensAndRedirect();
      return;
    }

    if (StorageService.getMobile().toString() !== paramMobile) {
      setIsVerifying(false);
      logout();
      return;
    }
    // access token, refresh token are stored in local storage
    // and no refresh token is provided in the url
    // and no mobile number is provided in the url
    validateTokensAndRedirect();
  }, []);

  useEffect(() => {
    if (paramMobile) setMobileNumber(paramMobile);
  }, []);
  async function requestForOTP(e) {
    e.preventDefault();
    if (Number(mobileNumber) % 1 != 0) {
      toast({
        description: 'Enter a valid mobile number',
        status: 'error',
        id: 'integer-mobile-number',
      });
      return;
    }
    if (mobileNumber.length !== 10) {
      toast({
        description: 'Enter a 10 digit mobile number',
        status: 'error',
        id: 'mobile-number',
      });
      return;
    }
    setIsOtpLoading(true);
    try {
      const token = await generateRecaptchaToken();
      const data = await otpCtrl.call({ mobileNumber, token });
      toast({ description: 'OTP sent!', status: 'success' });

      const seconds = differenceInSeconds(
        data.resend_sms_at * 1000,
        Date.now()
      );
      setResendSmsAt(Math.abs(seconds));
      setShowOtpForm(true);
    } catch (e) {
      validateBan(e);
    }
    setIsOtpLoading(false);
  }

  async function login(e) {
    e.preventDefault();
    try {
      const data = await loginCtrl.call({ mobileNumber, otp });

      setShowOtpForm(false);
      if (data.new_user === true) {
        setIsNewUser(true);
        return;
      }
      StorageService.setAccessToken(data.tokens.access_token);
      StorageService.setRefreshToken(data.tokens.refresh_token);
      StorageService.setUserId(data.user.userId);
      StorageService.setWId(data.wid);
      StorageService.setMobile(mobileNumber);
      if (StorageService.redirectUrl) {
        navigate(StorageService.redirectUrl);
        StorageService.redirectUrl = undefined;
        return;
      }
      navigate(PROFILE_PAGE);
    } catch (e) {
      validateBan(e);
    }
  }

  useInterval(
    () => {
      if (!otpCtrl.data?.resend_sms_at) return;

      setResendSmsAt(v => v - 1);
    },
    1000,
    [otpCtrl.data?.resend_sms_at]
  );
  if (isVerifying) return <Layout></Layout>;
  return (
    <Layout>
      <Flex
        alignItems={['flex-start', null, 'center']}
        justifyContent={'space-around'}
        flexDir={['column', null, 'row']}
        gap={8}
        margin="auto"
        minHeight={'75vh'}
      >
        <Flex
          flexDir={'column'}
          maxW={['100%', null, '50%']}
          pr={[0, 0, 0, 0, 24]}
          textAlign={['left']}
          mt={['70px', null, '0px']}
        >
          <Heading fontSize={['26px', '36px', '44px', '46px', '52px']}>
            Welcome User
          </Heading>
          <Text
            fontSize={['16px', '16px', '18px', '20px']}
            lineHeight={[6, 10]}
            opacity={0.6}
            color={['#883C1E']}
          >
            Log In to your Chillar Pro Wallet Now
          </Text>

          <Flex
            justifyContent={['center', 'flex-start']}
            display={['none', null, 'flex']}
            mt={10}
          >
            <CTA />
          </Flex>
        </Flex>

        <Flex
          flexDir={'column'}
          justifyContent={'space-evenly'}
          bg={'#FFE7D5'}
          py={[8, 16]}
          px={[4, 12]}
          borderRadius={'22px'}
          rowGap={8}
          w={['100%', null, '50%']}
          maxW="600px"
        >
          <Box>
            <Heading fontSize={['20px', '36px']} mb={3}>
              {showOtpForm ? 'OTP Verification' : 'Get Started'}
            </Heading>
            {showOtpForm && (
              <Text color="grey" mt={3}>
                Verify the OTP Sent to +91-{mobileNumber}
              </Text>
            )}
          </Box>
          {!showOtpForm ? (
            <form onSubmit={requestForOTP}>
              <FormLabel htmlFor="phone" fontSize={['16px', '20px']}>
                Mobile Number
              </FormLabel>

              <input
                ref={inputRef}
                key="mobile"
                className={clsx(
                  classes.mobileInput,
                  accountBlocked && classes.bannedInput
                )}
                onChange={e => setMobileNumber(e.target.value)}
                value={mobileNumber}
                placeholder={'Enter a 10 Digit Mobile Number'}
                type="number"
                id="phone"
                disabled={refreshToken || accountBlocked ? true : false}
                name="phone"
              />

              {!accountBlocked && (
                <Text color="grey" mt={3}>
                  You will receive an OTP of 6 Digits
                </Text>
              )}

              {isNewUser && !accountBlocked && (
                <Text color="red" mt={3}>
                  Please register on the mobile app to redeem
                </Text>
              )}
              {!accountBlocked ? (
                <SKButton
                  isLoading={otpCtrl.isLoading || isOtpLoading}
                  type="submit"
                  w={'170px'}
                  mt={12}
                  color="white"
                  disabled={
                    refreshToken || otpCtrl.isLoading || isOtpLoading
                      ? true
                      : false
                  }
                >
                  {refreshToken || otpCtrl.isLoading || isOtpLoading ? (
                    <Spinner />
                  ) : (
                    'Get OTP'
                  )}
                </SKButton>
              ) : (
                <>
                  <div className={classes.bannedText}>
                    Your account has been suspended for security purposes.
                  </div>
                  <BannedContactSupport mobile={mobileNumber} />
                </>
              )}
            </form>
          ) : (
            <form onSubmit={login}>
              <HStack>
                <PinInput
                  onChange={value => {
                    setOtp(value);
                  }}
                  autoFocus
                  placeholder=""
                  colorScheme="white"
                  focusBorderColor="white"
                  otp
                  bg="#FFFFFF"
                >
                  <PinInputField bg="#FFFFFF" />
                  <PinInputField bg="#FFFFFF" />
                  <PinInputField bg="#FFFFFF" />
                  <PinInputField bg="#FFFFFF" />
                  <PinInputField bg="#FFFFFF" />
                  <PinInputField bg="#FFFFFF" />
                </PinInput>
              </HStack>
              {resendSmsAt > 1 ? (
                <Box my={6}>
                  Resend OTP in {secondsToMinutes(Math.abs(resendSmsAt))}
                </Box>
              ) : (
                <Box my={4}>
                  <Text my={3}>Didn’t receive verification code?</Text>
                  <span
                    onClick={async e => {
                      e.preventDefault();
                      setResendSmsAt(null);
                      requestForOTP(e);
                    }}
                    style={{
                      color: 'var(--primary-color)',
                      cursor: 'pointer',
                    }}
                  >
                    Resend Code
                  </span>
                </Box>
              )}
              <div>
                <SKButton
                  style={{ width: 150 }}
                  isLoading={loginCtrl.isLoading}
                  htmlType="submit"
                  type="submit"
                  disabled={otp.length !== 6}
                  color="white"
                >
                  Login
                </SKButton>
              </div>
            </form>
          )}
        </Flex>

        <Flex justifyContent={'center'} display={['flex', null, 'none']}>
          <CTA />
        </Flex>
      </Flex>
    </Layout>
  );
}
